/**
 * P0-WOW-02 — Post-prediction WOW moment.
 *
 * Idempotent per session. Emits `prediction_wow_shown` (nation-emphasized
 * sonner toast + light celebration + auto-focus on ActivationBanner).
 * Points are surfaced as secondary info. The companion listener in
 * `src/lib/activation.ts` measures `prediction_to_share_seconds` from this
 * event to the first downstream share success.
 */
import { toast } from "sonner";
import { celebrate } from "@/lib/pr32/celebrationEngine";
import { trackEvent } from "@/lib/audit";
import { getNationFlag, getNationName } from "@/lib/share/nationWarsShare";
import { recordEngagement } from "@/lib/push/pushPermission";
import { finalizeReferralAfterFirstPrediction } from "@/lib/antifraud/antifraud.functions";

const FIRED_KEY = "vs:wow:prediction:fired";
const DISMISS_KEY = "vs:activation:dismissed";
const FOCUS_EVENT = "vs:wow:focus-activation";
const POINTS_DELTA = 5;

function alreadyFired(): boolean {
  try { return sessionStorage.getItem(FIRED_KEY) === "1"; } catch { return false; }
}
function markFired() {
  try { sessionStorage.setItem(FIRED_KEY, "1"); } catch { /* noop */ }
}

export interface FirePredictionWowInput {
  /** ISO-3 nation code from `useSelectedNation()`. Optional — fallback message shown when null. */
  nationCode?: string | null;
  /** Optional match id for telemetry. */
  matchId?: string | null;
}

/** Fire the WOW. Safe to call repeatedly — only the first call per session runs. */
export function firePredictionWow(input: FirePredictionWowInput = {}): void {
  if (typeof window === "undefined") return;
  if (alreadyFired()) return;
  markFired();

  const code = input.nationCode ?? null;
  const flag = getNationFlag(code);
  const name = code ? getNationName(code) : "tu nación";

  // Nation is the hero. Points are secondary info on the description line.
  const title = code
    ? `${flag} Tu predicción acaba de fortalecer a ${name}.`
    : `${flag} Tu predicción acaba de sumar para tu nación.`;
  const description = `+${POINTS_DELTA} puntos para ${name}`;

  void trackEvent("prediction_wow_shown", {
    nation_code: code,
    match_id: input.matchId ?? null,
    points_delta: POINTS_DELTA,
  });

  // PR-33 — D1 retention: after first prediction, allow SmartPushPrompt
  // to surface (READY_THRESHOLD=2). Additive, never asks on boot.
  try { recordEngagement("prediction_submit"); } catch { /* noop */ }

  // PR-34A — Anti-Fraud Foundation: a referral is only completed once the
  // referred user has (a) verified email and (b) made >=1 real prediction.
  // Best-effort, no blocking, no UX change.
  try { void finalizeReferralAfterFirstPrediction(); } catch { /* noop */ }

  toast.success(title, { description, duration: 4500 });

  // Light celebration (no modal, no blocking UI).
  setTimeout(() => {
    try { celebrate("points_won", { vibrate: true }); } catch { /* noop */ }
  }, 120);

  // Re-enable ActivationBanner if previously dismissed and ask it to pulse.
  try { localStorage.removeItem(DISMISS_KEY); } catch { /* noop */ }
  setTimeout(() => {
    try { window.dispatchEvent(new CustomEvent(FOCUS_EVENT)); } catch { /* noop */ }
    try {
      const el = document.querySelector<HTMLElement>("[data-activation-banner]");
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch { /* noop */ }
  }, 500);
}

export const WOW_FOCUS_EVENT = FOCUS_EVENT;